import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { NodeType, Pipeline } from '@ui/__generated__/graphql'
import emptyPipelineIllustration from '@ui/assets/illustrations/pipeline_empty.svg'
import { Button } from '@ui/components/core'
import { TableList } from '@ui/components/elements'
import { TableListHeader } from '@ui/components/elements/TableList'
import { showToast } from '@ui/components/elements/ToastManager'
import { Member } from '@ui/interfaces'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { removeTypename } from '@ui/lib/utils'
import { DeletePipelineMutation, GetAllPipelinesQuery, UpdatePipelineMutation } from '@ui/loaders/workflow'
import { useAuth } from '@ui/providers/AuthProvider.tsx'
import { RouteNamesEnum } from '@ui/router/routes'
import { pipelineService } from '@ui/services'

// query both REST (authorization) and GraphQL (complete pipeline data)
const useGetPipelines = (member: Member) => {
  // authorized pipelines with no workflow data
  const {
    data: getPipelinesRESTData,
    isLoading: getPipelinesRESTIsLoading,
    error: getPipelinesRESTError,
  } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => pipelineService.getPipelines(member),
  })

  // unauthorized workflow data
  const { data, loading, error } = GetAllPipelinesQuery()

  // combine the two
  const authorizedPipelineWorkflows =
    data?.allPipelineWorkflows && getPipelinesRESTData
      ? data.allPipelineWorkflows.filter((pipeline) => getPipelinesRESTData.some((p) => p.id === pipeline.id))
      : []

  return {
    data: authorizedPipelineWorkflows,
    loading: getPipelinesRESTIsLoading || loading,
    error: getPipelinesRESTError || error,
  }
}

export const AllPipelines_REST = () => {
  const { navigateTo } = useAppNavigation()
  const { instanceId } = useParams<{ instanceId: string }>()

  const { isDemoMode, currentMember } = useAuth()

  const { data, loading, error } = useGetPipelines(currentMember as Member)

  const [updatePipeline] = UpdatePipelineMutation()
  const [deletePipeline] = DeletePipelineMutation()

  const [pipelines, setFetchedPipelines] = useState<Pipeline[]>([])

  // We only want to get export nodes and their destinations
  const getUniqueDestinations = (pipeline: Pipeline) => {
    return pipeline.nodes
      ?.filter((node) => node?.type === NodeType.Export)
      .map((dest) => dest?.data?.exportType)
      .filter((s): s is string => Boolean(s))
  }

  const pipelineItems = useMemo(() => {
    return data.map((pipeline) => ({
      id: pipeline.id,
      name: pipeline.name,
      status: pipeline.enabled,
      destinations: getUniqueDestinations(pipeline),
    }))
  }, [data])

  const toggleEnablePipeline = useCallback(
    async (pipelineId: string) => {
      const pipeline = data.find((p) => p.id === pipelineId)
      if (!pipeline) return

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { nodes, edges, ...pipelineRest } = pipeline

      try {
        await updatePipeline({
          variables: {
            pipeline: {
              ...removeTypename(pipelineRest),
              enabled: !pipeline.enabled,
            },
          },
        })
      } catch (error) {
        showToast.error(`Failed to ${pipeline.enabled ? 'enable' : 'disable'} pipeline ${pipeline.name}`)
      }
    },
    [data, updatePipeline]
  )

  const handleDeletePipeline = useCallback(
    async (pipelineId: string) => {
      const pipeline = pipelines.find((p) => p.id === pipelineId)
      if (!pipeline) return

      try {
        deletePipeline({
          variables: {
            pipelineId: pipeline.id,
          },
        })

        showToast.success(`Pipeline ${pipeline.name} deleted`)
        setFetchedPipelines(pipelines.filter((p) => p.id !== pipeline.id))
      } catch (error) {
        showToast.error(`Failed to delete pipeline`)
      }
    },
    [deletePipeline, setFetchedPipelines, pipelines]
  )

  const handleSelectPipeline = ({ pipelineId }: { pipelineId: string }) => {
    navigateTo(RouteNamesEnum.EXISTING_PIPELINE, { pipelineId, instanceId })
  }

  const menuItems = useMemo(() => {
    return [
      {
        label: 'Delete',
        onClick: (pipelineId: string) => handleDeletePipeline(pipelineId),
      },
      {
        label: 'Toggle Status',
        onClick: (pipelineId: string) => toggleEnablePipeline(pipelineId),
      },
    ]
  }, [handleDeletePipeline, toggleEnablePipeline])

  if (loading || !data) {
    return <div>Loading...</div>
  }

  if (error) {
    return null
  }

  return (
    <div className="size-full">
      {pipelineItems.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center space-y-4">
          <img src={emptyPipelineIllustration} alt="No pipelines" className="h-40 w-1/2" />
          <p className="text-center text-gray-500">No pipelines found</p>
          <Button size="small" onClick={() => navigateTo(RouteNamesEnum.NEW_PIPELINE)}>
            Create your first pipeline
          </Button>
        </div>
      ) : (
        <>
          <div className="size-full space-y-4">
            <TableListHeader
              title="Pipelines"
              length={pipelines.length}
              action={
                isDemoMode ? null : (
                  <Button size="small" onClick={() => navigateTo(RouteNamesEnum.NEW_PIPELINE)}>
                    Add new pipeline
                  </Button>
                )
              }
            />

            <TableList
              items={pipelineItems!}
              logoColumns={['destinations']}
              menuItems={menuItems}
              onRowClick={(pipelineId) => handleSelectPipeline({ pipelineId })}
            />
          </div>
        </>
      )}
    </div>
  )
}
